import React, { useMemo } from 'react';

import DropdownMenu from '../../DropdownMenu';

import infoIcon from '../../../assets/images/my-meetings/info.svg';
import shareIcon from '../../../assets/images/my-meetings/share.svg';
import editIcon from '../../../assets/images/my-meetings/edit2.svg';
import deleteIcon from '../../../assets/images/my-meetings/delete.svg';
import downloadIcon from '../../../assets/images/my-meetings/download.svg';
import usersIcon from '../../../assets/images/my-meetings/users.svg';
import linkIcon from '../../../assets/images/my-meetings/link.svg';
import reportIcon from '../../../assets/images/my-meetings/report.svg';
import arrowUpIcon from '../../../assets/images/my-meetings/arrow-up.svg';

import { useUiContext } from '../../../context/UiContext';
import useCheckMobileScreen from '../../../hooks/useCheckMobileScreen';

const ActionsMenu = ({
  handleDetails,
  handleDelete,
  handleEdit,
  handleRequestReview,
  handleManageUsers,
  handleDownload,
  handleCopyLink,
  handleGetViewerReport,
  handleMoveToTrash,
  handleRestore,
  handleSetReminder,
  handleMove,
  enableMobileDelete = false,
  isNotAllowedToDelete = true,
  isNotAllowedToEdit = true,
  isNotAllowedToRequestReview = true,
  isNotAllowedToDownload = true,
  isNotAllowedToCheckDetails = true,
  isNotAllowedToManageUsers = true,
  isNotAllowedToCopyLink = true,
  isNotAllowedToGetViewerReport = true,
  isNotAllowedToSetReminder = true,
  isTrashBin,
  disabled,
  disabledTooltip,
}) => {
  const { notifySuccess } = useUiContext();
  const isMobile = useCheckMobileScreen();

  const options = useMemo(() => {
    const actions = [
      ...(handleRequestReview
        ? [
            {
              label: 'Request Review',
              onClick: () => handleRequestReview(),
              icon: <img src={shareIcon} alt="Request Review" />,
              visible: !isNotAllowedToRequestReview,
            },
          ]
        : []),
      {
        label: 'Edit',
        onClick: () => handleEdit(),
        icon: <img src={editIcon} alt="Edit" />,
        visible: !isNotAllowedToEdit,
      },
      ...(handleMove
        ? [
            {
              label: 'Move',
              onClick: () => handleMove(),
              icon: <img src={arrowUpIcon} alt="Move" />,
              visible: true,
            },
          ]
        : []),
      {
        label: 'Manage Access',
        onClick: () => handleManageUsers(),
        icon: <img src={usersIcon} alt="Manage Access" />,
        visible: !isNotAllowedToManageUsers,
      },
      {
        label: 'Details',
        onClick: () => handleDetails(),
        icon: <img src={infoIcon} alt="Details" style={{ left: 6 }} />,
        visible: !isNotAllowedToCheckDetails,
      },
      {
        label: 'Delete',
        onClick: () => handleMoveToTrash(),
        icon: <img src={deleteIcon} alt="Delete" />,
        visible: !isNotAllowedToDelete,
      },
      {
        label: 'Copy link',
        onClick: () => handleCopyLink(),
        icon: <img src={linkIcon} alt="Copy link" />,
        visible: !isNotAllowedToCopyLink,
      },
      {
        label: 'Viewer Report',
        onClick: () => handleGetViewerReport(),
        icon: <img src={reportIcon} alt="Viewer Report" />,
        visible: !isNotAllowedToGetViewerReport,
      },
      {
        label: 'Download',
        onClick: () => {
          handleDownload();
          notifySuccess(
            'Downloading started. Please wait for the download to complete.'
          );
        },
        icon: <img src={downloadIcon} alt="Download" style={{ left: -4 }} />,
        visible: !isNotAllowedToDownload,
      },
      {
        label: 'Set Reminder',
        onClick: handleSetReminder,
        icon: <img src={infoIcon} alt="Details" style={{ left: 6 }} />,
        visible: !isNotAllowedToSetReminder,
      },
    ];

    // Filter out hidden actions
    const visibleActions = actions.filter((action) => action.visible !== false);

    if (isMobile) {
      // Mobile: keep existing behavior (Details-only), optionally include Delete
      const detailsAction = visibleActions.find((a) => a.label === 'Details');
      const deleteAction = enableMobileDelete
        ? visibleActions.find((a) => a.label === 'Delete')
        : null;
      return [detailsAction, deleteAction].filter(Boolean);
    }

    return visibleActions.filter((action) => action.label !== 'Edit');
  }, [
    isNotAllowedToEdit,
    isNotAllowedToManageUsers,
    isNotAllowedToCheckDetails,
    isNotAllowedToDelete,
    isNotAllowedToCopyLink,
    isNotAllowedToGetViewerReport,
    isNotAllowedToDownload,
    handleSetReminder,
    isNotAllowedToSetReminder,
    isNotAllowedToRequestReview,
    isMobile,
    enableMobileDelete,
    handleMove,
    handleRequestReview,
    handleEdit,
    handleManageUsers,
    handleDetails,
    handleMoveToTrash,
    handleCopyLink,
    handleGetViewerReport,
    handleDownload,
    notifySuccess,
  ]);

  const trashBinOptions = useMemo(() => {
    return [
      {
        label: 'Restore',
        onClick: () => handleRestore(),
        icon: <img src={linkIcon} alt="Copy link" />,
      },
      {
        label: 'Delete Permanently',
        onClick: () => handleDelete(),
        icon: <img src={deleteIcon} alt="Delete" />,
      },
    ];
  }, [handleDelete, handleRestore]);

  const currentOptions = isTrashBin ? trashBinOptions : options;

  return <DropdownMenu options={currentOptions} isTrashBin={isTrashBin} disabled={disabled} disabledTooltip={disabledTooltip} />;
};

export default ActionsMenu;
